import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Image from 'next/image';
import Button from '../Button';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import logoVisitSweden from '../../public/img/logotype-visitsweden.svg';
import logoSweden from '../../public/img/logotype-sweden.svg';

import styles from './Footer.module.scss';

const Footer = ({
    footerMainMenu = [],
    footerPrivacyMenu = {},
    socialMediaProfiles = [],
    siteSwitcher = {},
    newsletterSignup = {},
}) => {
    const { t } = useTranslation('common');

    // Temporarily combine main menu and privacy menu items so no items are lost
    const combinedMenuItems = [...footerMainMenu, ...(footerPrivacyMenu?.items || [])];

    return (
        <footer className={styles['Footer']}>
            <div className={styles['Footer__Container']}>
                <SwedenLogotypes copyrightYear={footerPrivacyMenu?.copyrightYear} />
                <div className={styles['Footer__NavContainer']}>
                    {newsletterSignup?.newsletterUrl && (
                        <div className={styles['Footer__NewsletterMobile']}>
                            <Button
                                type="Primary"
                                icon="DiagonalArrow"
                                href={newsletterSignup.newsletterUrl}
                                target="_blank"
                                rel="noopener noreferrer">
                                {newsletterSignup.title || t('newsletterSignup.buttonText')}
                            </Button>
                        </div>
                    )}
                    <MainNav items={combinedMenuItems} />
                    <GlobalSiteSwitcher {...siteSwitcher} />
                </div>
                <div className={styles['Footer__SocialNewsletterContainer']}>
                    <div className={styles['Footer__SocialContainer']}>
                        <SocialNav profiles={socialMediaProfiles} />
                    </div>
                    {newsletterSignup?.newsletterUrl && (
                        <div className={styles['Footer__NewsletterDesktop']}>
                            <Button
                                type="Primary"
                                icon="DiagonalArrow"
                                href={newsletterSignup.newsletterUrl}
                                target="_blank"
                                rel="noopener noreferrer">
                                {newsletterSignup.title || t('newsletterSignup.buttonText')}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </footer>
    );
};

Footer.propTypes = {
    footerMainMenu: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            href: PropTypes.string.isRequired,
        })
    ),
    footerPrivacyMenu: PropTypes.object,
    socialMediaProfiles: PropTypes.array,
    siteSwitcher: PropTypes.object,
    newsletterSignup: PropTypes.object,
    hasTopSpacing: PropTypes.bool,
};

const SocialNav = ({ profiles = [] }) => {
    const { t } = useTranslation('common');
    return (
        <nav aria-labelledby="socialFooterNav" className={styles['Footer__SocialNav']}>
            <h2 id="socialFooterNav" className={styles['Footer__SocialNav__Heading']}>
                {t('footer.followus')}
            </h2>
            <div className={styles['Footer__SocialNav__Container']}>
                {profiles?.map((profile, i) => (
                    <a
                        key={i}
                        href={profile.url}
                        className={`${styles['Footer__SocialNav__Link']} ${
                            styles[`Footer__SocialNav__Link--${profile.icon}`]
                        }`}
                        aria-label={profile.icon}
                    />
                ))}
            </div>
        </nav>
    );
};

SocialNav.propTypes = {
    profiles: PropTypes.arrayOf(
        PropTypes.shape({
            icon: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired,
        })
    ).isRequired,
};

const MainNav = ({ items }) => {
    const { t } = useTranslation('common');
    return (
        <nav aria-labelledby="mainFooterNav" className={styles['Footer__MainNav']}>
            <h2 className={styles['Footer__MainNav__Heading']} id="mainFooterNav">
                {t('footer.about')}
            </h2>
            <ul className={styles['Footer__MainNav__Container']}>
                {items.map((item, i) => (
                    <li key={i} className={styles['Footer__MainNav__Item']}>
                        <a className={styles['Footer__MainNav__Link']} href={item.href}>
                            {item.title}
                        </a>
                    </li>
                ))}
            </ul>
        </nav>
    );
};

MainNav.propTypes = {
    items: PropTypes.array.isRequired,
};

const SwedenLogotypes = ({ copyrightYear }) => {
    const { t } = useTranslation('common');
    return (
        <div className={styles['Footer__SwedenLogotypes']}>
            <div className={styles['Footer__SwedenLogotypes__Wrapper']}>
                <a
                    className={styles['Footer__SwedenLogotypes__Link']}
                    href="https://sweden.se/"
                    aria-label="VisitSweden.com"
                >
                    <Image
                        {...logoSweden}
                        overrideSrc={logoSweden?.src}
                        className={styles['Footer__SwedenLogotypes__Logo']}
                        alt="Sweden - The Official Site"
                    />
                </a>
                <Image
                    {...logoVisitSweden}
                    overrideSrc={logoVisitSweden?.src}
                    className={styles['Footer__SwedenLogotypes__Logo']}
                    alt="VisitSweden.com"
                />
            </div>
            <div className={styles['Footer__PrivacyNav__CopyrightNotice']}>
                {t('footer.copyright', { copyrightYear })}
            </div>
        </div>
    );
};

const PrivacyNav = ({ items = [] }) => {
    const { t } = useTranslation('common');
    return (
        <nav aria-labelledby="privacyFooterNav" className={styles['Footer__PrivacyNav']}>
            <ul className={styles['Footer__PrivacyNav__Container']}>
                {items.map((item, i) => (
                    <li key={i} className={styles['Footer__PrivacyNav__Item']}>
                        <a className={styles['Footer__PrivacyNav__Link']} href={item.href}>
                            {item.title}
                        </a>
                    </li>
                ))}
            </ul>
        </nav>
    );
};

PrivacyNav.propTypes = {
    items: PropTypes.array.isRequired,
};

const GlobalSiteSwitcher = ({ label = '', links = [] }) => {
    if (!links.length) {
        return null;
    }

    return (
        <nav className={styles['Footer__GlobalSiteSwitcher']} aria-labelledby="global-site-switcher-button">
            <div className={styles['Footer__GlobalSiteSwitcher__Content']}>
                <h2 className={styles['Footer__GlobalSiteSwitcher__Heading']}>{label}</h2>
                <ul className={classNames(styles['Footer__GlobalSiteSwitcher__Items'])}>
                    {links.map(({ value }, i) => (
                        <li key={i}>
                            <a className={styles['Footer__GlobalSiteSwitcher__Link']} href={value.href}>
                                {value.title}
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
        </nav>
    );
};

GlobalSiteSwitcher.propTypes = {
    label: PropTypes.string,
    links: PropTypes.array,
};

export default Footer;
